import {cloneDeep, debounce, find, isEmpty, isEqual, pick} from 'lodash'
import React, {
  forwardRef,
  memo,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import OrderService from '../../core/_requests'
import {
  OrderStatus,
  OrderChildModalName,
  OrderChildModalNameType,
  OrderType,
  VALID_FEDEX_CONTENT_TYPES,
  VALID_GENERAL_CONTENT_TYPES,
  OVERLAPPING_CONTENT_TYPES,
  INSURANCE_TYPE,
} from '../../core/_const'
import useCancelToken from '../../../../../_gori/hooks/UseCancelToken'
import {Rates} from './Rates'
import clsx from 'clsx'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {convertCurrency, KTSVG, toAbsoluteUrl} from '../../../../../_gori/helpers'
import {Button, SelectFormik} from '../../../../../_gori/partials/widgets'
import {InsuranceSignature} from './InsuranceSignature'
import {useStatesGlobal} from '../../../../../_gori/helpers/components/StatesGlobalProvider'
import {useIntl} from 'react-intl'
import OrderHelper from '../../core/_helpers'
import {EmptyRate} from './EmptyRate'
import {LAPTOP_MIN_WIDTH, ROUTES} from '../../../../../_gori/constants'
import {Link} from 'react-router-dom'
import SettingsService from '../../../settings/core/_requests'
import {INSURANCE_AMOUNT_MAX, SETTING_INSURANCE_TYPE} from '../../../settings/core/_const'
import {useCarrierNames, useCarrierRateFetcher} from '../../hooks'
import {
  buildRatePayload,
  getCarrierNameFromServiceKey,
  isCarrierApplicableForRoute,
  isInternationalByAddress,
} from './rateHelpers'
import useWindowDimensions from '../../../../../_gori/hooks/useWindowDimensions'

type Loading = {rate: boolean; updateRate: boolean}

type OrderChildRateProps = {
  formik: any
  setValidationErrors: (error: any) => void
  setRateLoading: (loading: boolean) => void
  id?: number | string
  data?: any
  isDisablePage: boolean
  bottomComponent?: ReactElement
  name?: OrderChildModalNameType
  setIsAccept: (value: boolean) => void
  isAccept: boolean
  isModal?: boolean
  setIsErrorUpicAccept: (value: boolean) => void
  isErrorUpicAccept: boolean
}
export type OrderChildRateForwardRefProps = {
  handleGetRates: () => void
  dataRates: () => any
  setDataRates: (data: any) => void
  loading: () => Loading
  serviceHasPackage: () => boolean
  setPresetService: (service: string | undefined) => void
}

export const OrderChildRate = memo(
  forwardRef<OrderChildRateForwardRefProps, OrderChildRateProps>(
    (
      {
        formik,
        setValidationErrors,
        id,
        data,
        isDisablePage,
        bottomComponent = null,
        setRateLoading,
        name,
        setIsAccept,
        isAccept,
        isModal = false,
        isErrorUpicAccept = false,
        setIsErrorUpicAccept,
      },
      ref
    ) => {
      const {
        newCancelToken,
        isCancel,
        cancelAndCreateNewToken: cancelActiveRequestAndCreateNewToken,
      } = useCancelToken()
      const intl = useIntl()
      const {width} = useWindowDimensions()
      const [canGetRates, setCanGetRates] = useState<boolean>(false)
      const [dataRates, setDataRates] = useState<any>()
      const [presetService, setPresetServiceState] = useState<string | undefined>(undefined)
      const [forceRateRefresh, setForceRateRefresh] = useState<number>(0)
      const dataRatesRef = useRef<any>()
      const currentRates = useRef<any>(null)
      const previousContentTypeCategory = useRef<string | null>(null)
      const previousContentType = useRef<string | null>(null)
      const additionalFetchedCarriers = useRef<Set<string>>(new Set())
      const hasUserExpandedRates = useRef(false)
      const pendingRateRefresh = useRef(false)
      const previousRateLoading = useRef<boolean | null>(null)
      const isMounted = useRef(true)

      // Ref to read presetService inside callbacks without stale closure issues.
      // Updated synchronously in setPresetService so handleGetRates always reads
      // the latest value even before React re-renders.
      const presetServiceRef = useRef<string | undefined>(undefined)

      const setPresetService = useCallback((service: string | undefined) => {
        presetServiceRef.current = service
        // Clear tracked carriers so the preset-only branch is entered on next fetch.
        // This resets the "expanded beyond preset" state.
        additionalFetchedCarriers.current.clear()
        hasUserExpandedRates.current = false
        setPresetServiceState(service)
      }, [])

      // Ref to read formik.values inside callbacks without adding it to deps.
      // Prevents infinite loops when a callback both reads and writes the same formik value.
      // NOTE: Callbacks using this ref always read the LATEST form state (not the state
      // at callback creation time), which is intentional for rate fetching.
      const formikValuesRef = useRef(formik.values)
      formikValuesRef.current = formik.values

      // Helper to determine content type category
      const getContentTypeCategory = useCallback(
        (contentType: string | undefined): string | null => {
          if (!contentType) return null
          if (VALID_FEDEX_CONTENT_TYPES.includes(contentType)) return 'fedex'
          if (VALID_GENERAL_CONTENT_TYPES.includes(contentType)) return 'general'
          return null
        },
        []
      )

      const {statesGlobal} = useStatesGlobal()
      const orderHelper = OrderHelper()

      const carriers = statesGlobal.carriers
      const addOnServices = statesGlobal.addOnServices
      const carrierRouteSettings = statesGlobal.carrierRouteSettings

      // Look up the preset's original service from presets data
      const presetOriginalService = useMemo(() => {
        if (!data?.preset_id || !statesGlobal?.presets) return undefined
        const preset = (statesGlobal.presets as any[]).find((p: any) => p.id === data.preset_id)
        return preset?.shipping_options?.service || undefined
      }, [data?.preset_id, statesGlobal?.presets])

      useEffect(() => {
        if (presetOriginalService) {
          setPresetService(presetOriginalService)
        }
      }, [presetOriginalService, setPresetService])

      const {carrierNames, disabledCarrierNames} = useCarrierNames({
        isInternational: formik.values.international,
      })

      const handleRateMerge = useCallback((options: any[], _chooseRate: any) => {
        const nextDataRates = {...dataRatesRef.current, options}
        dataRatesRef.current = nextDataRates
        setDataRates(nextDataRates)
      }, [])

      const {
        loadingCarriers,
        retryingCarriers,
        fetchOne: fetcherFetchOne,
        retryOneKeepVisible,
        cancelAll: fetcherCancelAll,
        dataRatesRef: fetcherDataRatesRef,
        nextRequestId,
        pendingCarriersRef,
        requestIdRef,
      } = useCarrierRateFetcher({
        onMerge: handleRateMerge,
        onError: (response) => {
          setValidationErrors(response)
        },
        useRequestIdGuard: true,
      })

      const [loading, setLoading] = useState<Loading>({
        rate: false,
        updateRate: false,
      })

      const cancelAndCreateNewToken = useCallback(() => {
        nextRequestId()
        pendingRateRefresh.current = false
        fetcherCancelAll()
        return cancelActiveRequestAndCreateNewToken()
      }, [cancelActiveRequestAndCreateNewToken, fetcherCancelAll, nextRequestId])

      const [insuranceAutoSetting, setInsuranceAutoSetting] = useState<any>({
        status: false,
        value: '',
        type: '',
      })
      const [statusInsuranceSetting, setStatusInsuranceSetting] = useState(false)
      const insuranceSettingLoaded = useRef(false)
      const [isInsuranceSettingLoaded, setIsInsuranceSettingLoaded] = useState(false)

      const getSettingInsurance = useCallback(async () => {
        const response = await SettingsService.getSettingInsurance()
        if (response) {
          insuranceSettingLoaded.current = true
          setIsInsuranceSettingLoaded(true)
          setInsuranceAutoSetting({
            status: response?.data?.status ?? false,
            value: response?.data?.value ?? 0,
            type: response?.data?.type ?? '',
          })
        }
      }, [])

      useEffect(() => {
        getSettingInsurance()
      }, [getSettingInsurance])

      useEffect(() => {
        dataRatesRef.current = dataRates
        fetcherDataRatesRef.current = dataRates
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [dataRates])

      useEffect(() => {
        const isRateLoading = loadingCarriers.length > 0
        if (previousRateLoading.current === isRateLoading) return

        previousRateLoading.current = isRateLoading
        setRateLoading(isRateLoading)
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [loadingCarriers.length])

      useEffect(() => {
        return () => {
          isMounted.current = false
          fetcherCancelAll()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [])

      const totalPriceItems = useMemo(() => {
        const items = Object.values(formik.values?.order_items || {})

        const total = items.reduce((sum, item: any) => {
          const quantity = Number(item?.quantity) || 0
          const unitPrice = Number(item?.unit_price) || 0
          // @ts-ignore
          return sum + quantity * unitPrice
        }, 0)

        return Math.min(total as number, INSURANCE_AMOUNT_MAX)
      }, [formik.values.order_items])

      const prevAmountRef = useRef()

      useEffect(() => {
        // Skip calculation until insurance setting has been loaded from API
        if (!insuranceSettingLoaded.current) return

        const currentAmount = formik?.values?.insurance_option?.insured_amount
        const currentProvider = formik?.values?.insurance_option?.provider

        // Determine if banner should be shown
        let shouldShowBanner = false

        // Case 1: No automation is enabled
        if (!insuranceAutoSetting.status) {
          // Show banner only if no manual insurance is applied
          if (!currentProvider || currentProvider === null) {
            shouldShowBanner = true
          }
        }
        // Case 2: Automation is enabled for ALL packages
        else if (insuranceAutoSetting.type === SETTING_INSURANCE_TYPE.ALL_PACKAGES.TYPE) {
          // Hide banner - automation applies to all packages
          shouldShowBanner = false
        }
        // Case 3: Automation is enabled for HIGH_VALUE_ONLY
        else if (insuranceAutoSetting.type === SETTING_INSURANCE_TYPE.HIGH_VALUE_ONLY.TYPE) {
          const meetsThreshold = totalPriceItems >= insuranceAutoSetting.value

          if (currentProvider === INSURANCE_TYPE.BAE_GUARD && currentAmount > 0) {
            // User manually applied baeguard - hide banner
            shouldShowBanner = false
          } else if (meetsThreshold) {
            // Total price >= threshold - auto insurance will apply - hide banner
            shouldShowBanner = false
          } else {
            // Total price < threshold - shipment won't be covered - show banner
            shouldShowBanner = true
          }
        }

        setStatusInsuranceSetting(shouldShowBanner)
        prevAmountRef.current = currentAmount

        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
        insuranceAutoSetting,
        formik?.values?.insurance_option?.provider,
        formik?.values?.insurance_option?.insured_amount,
        totalPriceItems,
      ])

      const settingInsurance = useCallback(async () => {
        if (data?.status !== OrderStatus.SHIPPED && !data) {
          const settingValue = Number(insuranceAutoSetting.value) || 0
          if (!insuranceAutoSetting.status) {
            formik.setFieldValue('insurance_option.provider', null)
          } else {
            if (insuranceAutoSetting.type === SETTING_INSURANCE_TYPE.ALL_PACKAGES.TYPE) {
              formik.setFieldValue('insurance_option.provider', INSURANCE_TYPE.BAE_GUARD)
              formik.setFieldValue('insurance_option.insured_amount', settingValue)
            } else if (insuranceAutoSetting.type === SETTING_INSURANCE_TYPE.HIGH_VALUE_ONLY.TYPE) {
              if (settingValue < totalPriceItems) {
                setStatusInsuranceSetting(true)
                formik.setFieldValue('insurance_option.insured_amount', totalPriceItems)
                formik.setFieldValue('insurance_option.provider', INSURANCE_TYPE.BAE_GUARD)
              }
            }
          }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [insuranceAutoSetting, totalPriceItems, formik.values.order_items])

      useEffect(() => {
        settingInsurance()
      }, [settingInsurance])

      const handleGetRates = useCallback(
        async (skipCancel = false) => {
          if (!skipCancel) {
            cancelAndCreateNewToken()
          } else {
            nextRequestId()
          }
          pendingRateRefresh.current = false
          setLoading((prev) => ({...prev, rate: true}))

          const values = formikValuesRef.current

          const dataParcels = orderHelper.buildRateParcels(values?.service, values?.parcels, false)

          currentRates.current = pick(values, [
            'from',
            'to',
            'parcels',
            'international_option',
            'custom_items',
            'signature_confirmation',
            'extra_service',
            'insurance_option.insured_amount',
            'insurance_option.provider',
          ])

          const getDefaultRate = (rates) => {
            const rateOptions = Array.isArray(rates) ? rates : []
            const matchingPackages = rateOptions.filter((item) => item?.matching_package)
            return matchingPackages?.[0] || null
          }

          const applyChooseRate = (rates) => {
            const validRates = (Array.isArray(rates) ? rates : []).filter(
              (item) => item && !item?.error
            )
            if (!validRates.length) return null

            const defaultRate = getDefaultRate(validRates)
            const currentChooseRate = dataRatesRef.current?.chooseRate
            const selectedService =
              currentChooseRate?.carrier_service || formikValuesRef.current?.service || null

            let isDefaultRate = false
            let _chooseRate: any = {}

            if (selectedService) {
              _chooseRate = cloneDeep(validRates).find((item: any) => {
                return item?.matching_package && item?.carrier_service === selectedService
              })

              if (!_chooseRate) {
                _chooseRate = cloneDeep(validRates).find((item) => {
                  return item?.carrier_service === selectedService
                })
              }

              if (!_chooseRate) {
                const selectedCarrierName = getCarrierNameFromServiceKey(selectedService)
                if (pendingCarriersRef.current.includes(selectedCarrierName)) {
                  return currentChooseRate || null
                }

                if (!currentChooseRate && defaultRate) {
                  isDefaultRate = true
                }
              }
            } else if (defaultRate) {
              isDefaultRate = true
            }

            if (isDefaultRate) {
              _chooseRate = defaultRate
              formik.setFieldValue('service', defaultRate?.carrier_service || '')
              formik.setFieldValue('estimated_rate', defaultRate?.rate || null)
              formik.setFieldValue('is_cheapest_rate', defaultRate?.cheapest || false)
            }

            if (!_chooseRate && !isDefaultRate) {
              formik.setFieldValue('service', '')
              formik.setFieldValue('estimated_rate', null)
              formik.setFieldValue('is_cheapest_rate', false)
            } else if (
              _chooseRate &&
              _chooseRate?.carrier_service !== formikValuesRef.current?.service
            ) {
              formik.setFieldValue('service', _chooseRate?.carrier_service || '')
              formik.setFieldValue('estimated_rate', _chooseRate?.rate || null)
              formik.setFieldValue('is_cheapest_rate', _chooseRate?.cheapest || false)
            }

            formik.setFieldValue(
              'insurance_option.insured_fee',
              _chooseRate?.insurance_rate || null
            )

            return _chooseRate
          }

          const initialDataRates = {chooseRate: null, options: []}
          dataRatesRef.current = initialDataRates
          fetcherDataRatesRef.current = initialDataRates
          setDataRates(initialDataRates)

          // When a preset with a service is active, only fetch the preset's carrier initially.
          // The remaining carriers are fetched lazily when the user clicks "All Rates".
          // This applies to both form-triggered refetches AND manual refresh, as long as
          // the user hasn't expanded to see all carriers yet.
          const _presetCarrierName = presetServiceRef.current
            ? getCarrierNameFromServiceKey(presetServiceRef.current)
            : null

          // Determine if the user has previously fetched carriers beyond the preset.
          // If additionalFetchedCarriers only contains the preset carrier (or is empty),
          // we're still in preset-only mode and refresh should only fetch the preset.
          // Once the user has explicitly expanded (clicked "All Rates"), we stay expanded
          // even across form-triggered refetches.
          const hasExpandedBeyondPreset = hasUserExpandedRates.current
            ? true
            : _presetCarrierName
            ? Array.from(additionalFetchedCarriers.current).some(
                (name) => name !== _presetCarrierName
              )
            : false

          if (_presetCarrierName && !hasExpandedBeyondPreset) {
            // Preset-only mode: only fetch the preset carrier
            // But skip if the preset carrier is not applicable for the current route
            const presetApplicable = isCarrierApplicableForRoute(
              carriers,
              _presetCarrierName,
              values.international ?? false
            )

            if (!presetApplicable) {
              // Preset carrier not applicable for this route — skip preset-only mode
              // and fall through to fetch all active carriers normally
            } else {
              additionalFetchedCarriers.current.clear()
              // Track the preset carrier so fetchActiveCarriers won't re-fetch it
              additionalFetchedCarriers.current.add(_presetCarrierName)
              const carriersToFetch = [_presetCarrierName]

              const payloads = carriersToFetch.map((carrierName) =>
                buildRatePayload(values, dataParcels, carrierName)
              )

              fetcherDataRatesRef.current = initialDataRates

              const guardId = requestIdRef.current
              const fetchWithChooseRate = async (payload: any) => {
                await fetcherFetchOne(payload, guardId)
                if (requestIdRef.current === guardId) {
                  const currentOptions = Array.isArray(dataRatesRef.current?.options)
                    ? dataRatesRef.current.options
                    : []
                  const chooseRate = applyChooseRate(currentOptions)
                  const nextDataRates = {...dataRatesRef.current, chooseRate}
                  dataRatesRef.current = nextDataRates
                  fetcherDataRatesRef.current = nextDataRates
                  setDataRates(nextDataRates)
                }
              }

              await Promise.allSettled(payloads.map(fetchWithChooseRate))
              if (isMounted.current && requestIdRef.current === guardId) {
                setLoading((prev) => ({...prev, rate: false}))
              }
              return
            }
          }

          // Include any carriers that were manually fetched via "Get Rate"
          // Only on manual refresh (skipCancel=false), not on form-triggered refetches
          const additionalCarriers = skipCancel
            ? []
            : Array.from(additionalFetchedCarriers.current).filter(
                (name) =>
                  !carrierNames.includes(name) &&
                  isCarrierApplicableForRoute(carriers, name, values.international ?? false)
              )

          // Clear additional carriers on form-triggered refetches (shipment details changed)
          // but only if the user hasn't explicitly expanded to all rates
          if (skipCancel && !hasUserExpandedRates.current) {
            additionalFetchedCarriers.current.clear()
          }

          // Include the preset carrier if it's disabled (not in active carriers)
          if (
            _presetCarrierName &&
            !carrierNames.includes(_presetCarrierName) &&
            !additionalCarriers.includes(_presetCarrierName) &&
            isCarrierApplicableForRoute(carriers, _presetCarrierName, values.international ?? false)
          ) {
            additionalCarriers.push(_presetCarrierName)
            additionalFetchedCarriers.current.add(_presetCarrierName)
          }

          const allCarriersToFetch = [...carrierNames, ...additionalCarriers]

          // Track all carriers being fetched so fetchActiveCarriers won't re-fetch them
          for (const name of allCarriersToFetch) {
            additionalFetchedCarriers.current.add(name)
          }

          // If no carriers to fetch at all, skip requests
          if (!allCarriersToFetch.length) {
            setLoading((prev) => ({...prev, rate: false}))
            return
          }

          const payloads = allCarriersToFetch.map((carrierName) =>
            buildRatePayload(values, dataParcels, carrierName)
          )

          // Temporarily patch the hook's dataRatesRef to stay in sync
          fetcherDataRatesRef.current = initialDataRates

          // Use a local wrapper that applies chooseRate after each merge
          const guardId = requestIdRef.current
          const fetchWithChooseRate = async (payload: any) => {
            await fetcherFetchOne(payload, guardId)
            // After each fetch, the hook's onMerge fires which updates dataRatesRef
            // Then we apply chooseRate on top
            if (requestIdRef.current === guardId) {
              const currentOptions = Array.isArray(dataRatesRef.current?.options)
                ? dataRatesRef.current.options
                : []
              const chooseRate = applyChooseRate(currentOptions)
              const nextDataRates = {...dataRatesRef.current, chooseRate}
              dataRatesRef.current = nextDataRates
              fetcherDataRatesRef.current = nextDataRates
              setDataRates(nextDataRates)
            }
          }

          await Promise.allSettled(payloads.map(fetchWithChooseRate))
          if (isMounted.current && requestIdRef.current === guardId) {
            setLoading((prev) => ({...prev, rate: false}))
          }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
          cancelAndCreateNewToken,
          nextRequestId,
          orderHelper,
          setValidationErrors,
          carrierNames,
          fetcherFetchOne,
          fetcherDataRatesRef,
          requestIdRef,
          pendingCarriersRef,
        ]
      )

      const fetchAdditionalCarrierRates = useCallback(
        async (carrierName: string) => {
          additionalFetchedCarriers.current.add(carrierName)

          const values = formikValuesRef.current
          const dataParcels = orderHelper.buildRateParcels(values?.service, values?.parcels, false)

          const payload = buildRatePayload(values, dataParcels, carrierName)
          await fetcherFetchOne(payload)
        },
        [orderHelper, fetcherFetchOne]
      )

      const exploreAllRates = useCallback(async () => {
        setLoading((prev) => ({...prev, rate: true}))

        const values = formikValuesRef.current
        const dataParcels = orderHelper.buildRateParcels(values?.service, values?.parcels, false)

        await Promise.allSettled(
          disabledCarrierNames.map((carrierName) => {
            additionalFetchedCarriers.current.add(carrierName)
            const payload = buildRatePayload(values, dataParcels, carrierName)
            return fetcherFetchOne(payload)
          })
        )

        if (isMounted.current) {
          setLoading((prev) => ({...prev, rate: false}))
        }
      }, [orderHelper, fetcherFetchOne, disabledCarrierNames])

      /** Fetch only the active (opted-in) carriers that haven't been fetched yet.
       *  Triggered by the "All Rates" button when a preset is active. */
      const fetchActiveCarriers = useCallback(async () => {
        // Mark that the user has explicitly expanded beyond preset-only mode.
        // This persists across form-triggered refetches so rates stay expanded.
        hasUserExpandedRates.current = true

        const values = formikValuesRef.current
        const dataParcels = orderHelper.buildRateParcels(values?.service, values?.parcels, false)

        const carriersToFetch = carrierNames.filter(
          (name) => !additionalFetchedCarriers.current.has(name)
        )

        if (!carriersToFetch.length) return

        setLoading((prev) => ({...prev, rate: true}))

        await Promise.allSettled(
          carriersToFetch.map((carrierName) => {
            additionalFetchedCarriers.current.add(carrierName)
            const payload = buildRatePayload(values, dataParcels, carrierName)
            return fetcherFetchOne(payload)
          })
        )

        if (isMounted.current) {
          setLoading((prev) => ({...prev, rate: false}))
        }
      }, [orderHelper, fetcherFetchOne, carrierNames])

      const retryCarrierRates = useCallback(
        async (carrierName: string) => {
          const values = formikValuesRef.current
          const dataParcels = orderHelper.buildRateParcels(values?.service, values?.parcels, false)

          const payload = buildRatePayload(values, dataParcels, carrierName)
          await retryOneKeepVisible(payload)
        },
        [orderHelper, retryOneKeepVisible]
      )

      // Auto-fetch rates for the preset carrier when it's disabled (not in active carriers)
      // This is now handled inside handleGetRates which includes the preset carrier in the initial batch.
      // This effect only covers the edge case where presetService changes AFTER rates have already loaded
      // (e.g., user picks a different preset from the dropdown while rates are already displayed).
      const presetCarrierName = presetService ? getCarrierNameFromServiceKey(presetService) : null

      useEffect(() => {
        if (
          presetCarrierName &&
          disabledCarrierNames.includes(presetCarrierName) &&
          !loadingCarriers.includes(presetCarrierName) &&
          !loading.rate &&
          canGetRates &&
          !additionalFetchedCarriers.current.has(presetCarrierName)
        ) {
          fetchAdditionalCarrierRates(presetCarrierName)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [presetCarrierName, disabledCarrierNames, loading.rate, canGetRates])

      const debouncedGetRates = useMemo(() => {
        return debounce(() => {
          handleGetRates(true)
        }, 300)
      }, [handleGetRates])

      // Track content type category changes to force rate refresh
      // Only trigger refresh when switching BETWEEN categories (fedex <-> general), not within same category
      useEffect(() => {
        const currentContentType = formik.values?.international_option?.content
        const currentCategory = getContentTypeCategory(currentContentType)

        // Check if current content type is overlapping (valid in both categories)
        const isOverlapping =
          currentContentType && OVERLAPPING_CONTENT_TYPES.includes(currentContentType)

        // Only force rate refresh when category ACTUALLY changes (fedex <-> general)
        // Switching within same category (e.g., sold -> repair_and_return) should NOT trigger refresh
        const categoryActuallyChanged =
          previousContentTypeCategory.current !== null &&
          currentCategory !== null &&
          previousContentTypeCategory.current !== currentCategory

        if (categoryActuallyChanged && !isOverlapping) {
          // Reset currentRates to null so shouldRefreshRates returns true
          currentRates.current = null
          setForceRateRefresh((prev) => prev + 1)
        }

        // Track previous content type
        previousContentType.current = currentContentType || null

        if (currentCategory !== null) {
          if (!isOverlapping) {
            previousContentTypeCategory.current = currentCategory
          } else if (previousContentTypeCategory.current === null) {
            // Initialize with first category for overlapping types
            previousContentTypeCategory.current = currentCategory
          }
        }
      }, [formik.values?.international_option?.content, getContentTypeCategory])

      // Helper to compare values for rate refresh, excluding content type when within same category
      const shouldRefreshRates = useCallback(
        (currentValues: any, previousValues: any): boolean => {
          // If previousValues is null (reset by forceRateRefresh or first load), always refresh
          if (!previousValues) return true

          const currentContentType = currentValues?.international_option?.content
          const prevContentType = previousValues?.international_option?.content

          // If content type changed
          if (currentContentType !== prevContentType) {
            const currentCategory = getContentTypeCategory(currentContentType)
            const prevCategory = getContentTypeCategory(prevContentType)

            // If within same category, compare everything EXCEPT content type
            if (currentCategory === prevCategory && currentCategory !== null) {
              // Create copies without content field for comparison
              const currentWithoutContent = {
                ...currentValues,
                international_option: {
                  ...currentValues?.international_option,
                  content: '__IGNORE__',
                },
              }
              const prevWithoutContent = {
                ...previousValues,
                international_option: {
                  ...previousValues?.international_option,
                  content: '__IGNORE__',
                },
              }
              return !isEqual(currentWithoutContent, prevWithoutContent)
            }

            // Different categories (fedex <-> general) - always refresh rates
            return true
          }

          // Content type didn't change, do normal comparison
          return !isEqual(currentValues, previousValues)
        },
        [getContentTypeCategory]
      )

      const loadRate = useCallback(() => {
        const values = pick(formik.values, [
          'from',
          'to',
          'parcels',
          'international_option',
          'custom_items',
          'signature_confirmation',
          'extra_service',
          'insurance_option.insured_amount',
          'insurance_option.provider',
        ])

        const validateParcels = Object.values(formik.values?.parcels || {}).every((parcel: any) =>
          ['length', 'width', 'height', 'weight', 'package_count'].every(
            (field) => Number(parcel?.[field]) > 0
          )
        )
        const errors = Object.keys(formik.errors).map((item) => item)

        // For rate refresh, we should NOT block on international_option errors
        // because content type validation is service-dependent and rates should
        // still refresh when switching content types between categories
        const fillDataRequired = !errors.some((item) =>
          [
            'from',
            'to',
            'parcels',
            // 'international_option' removed - content type validation should not block rate refresh
            'custom_items',
            'signature_confirmation',
            'insurance_option',
            'extra_service',
          ].includes(item)
        )

        // For international orders, block rate fetching until the customs form is valid.
        // This prevents premature fetches when the address switches to international
        // but the customs form hasn't been filled in yet.
        // We derive international status directly from address values to avoid the timing
        // gap where formik.values.international hasn't been updated yet by the useEffect
        // in OrderChild that detects international addresses.
        const isInternational =
          formik.values?.international === true ||
          isInternationalByAddress(formik.values?.from, formik.values?.to)
        // For international orders, require that customs data is actually present and valid.
        // We can't rely solely on error absence because the validation schema may not have
        // been applied yet on the same render cycle where the address changes.
        // Check both: no validation errors AND customs content type is filled in.
        const hasCustomsContent = !!formik.values?.international_option?.content
        const hasCustomsItems = Object.values(formik.values?.custom_items || {}).some(
          (item: any) => !!item?.description && Number(item?.quantity) > 0
        )
        const customsValid =
          !isInternational ||
          (hasCustomsContent &&
            hasCustomsItems &&
            !errors.includes('custom_items') &&
            !errors.includes('international_option'))

        const _canGetRates = validateParcels && fillDataRequired && customsValid
        setCanGetRates(_canGetRates)

        // Only load rates if:
        // 1. Order status is OPEN
        // 2. Order status is SHIPPED and we're in reship mode (name is CREATE_LABEL)
        // 3. No data and no name (create new order)
        // 4. Order status is CANCELLED, parcels are valid,
        // and the user has already filled in the data, then we can get rates.
        const shouldLoadRates =
          data?.status === OrderStatus.OPEN ||
          (data?.status === OrderStatus.SHIPPED && name === OrderChildModalName.CREATE_LABEL) ||
          (data?.status === OrderStatus.CANCELLED && _canGetRates) ||
          (!data && !name)

        const shouldRefresh = shouldRefreshRates(values, currentRates.current)

        // Auto-fetch when the form transitions from invalid → valid while rates are cleared.
        // This covers the case where an international order's customs form becomes valid
        // but the shipment detail values haven't changed (shouldRefresh would be false).
        const ratesAreCleared = dataRatesRef.current?.options === undefined
        const shouldRecoverFromInvalid =
          !shouldRefresh && _canGetRates && shouldLoadRates && ratesAreCleared

        // Use custom comparison that ignores content type changes within same category
        if (shouldRefresh || shouldRecoverFromInvalid) {
          if (_canGetRates && shouldLoadRates) {
            if (loadingCarriers.length) {
              cancelAndCreateNewToken()
            }
            pendingRateRefresh.current = true
            debouncedGetRates()
          } else {
            if (loadingCarriers.length) {
              cancelAndCreateNewToken()
            } else {
              nextRequestId()
              fetcherCancelAll()
            }
            pendingRateRefresh.current = false
            debouncedGetRates.cancel()
            setLoading((prev) => (prev.rate ? {...prev, rate: false} : prev))
            // Clear stale rates so the empty state shows when form is invalid
            const clearedRates = {chooseRate: null, options: undefined}
            dataRatesRef.current = clearedRates
            fetcherDataRatesRef.current = clearedRates
            setDataRates(clearedRates)
            // Record current values so shouldRefreshRates returns false on the next
            // render cycle, preventing an infinite update loop when parcels are invalid.
            currentRates.current = values
          }
        }

        return debouncedGetRates.cancel

        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [formik.values, formik.errors, forceRateRefresh, debouncedGetRates])

      // Use a stable ref for loadRate so the useEffect doesn't re-fire
      // when handleGetRates is recreated (which would cause infinite loops).
      const loadRateRef = useRef(loadRate)
      loadRateRef.current = loadRate

      // Derive a stable key from the rate-relevant formik values.
      // The useEffect fires only when these shipment details actually change.
      const rateRelevantKey = useMemo(() => {
        return JSON.stringify(
          pick(formik.values, [
            'from',
            'to',
            'parcels',
            'international_option',
            'custom_items',
            'signature_confirmation',
            'extra_service',
            'insurance_option.insured_amount',
            'insurance_option.provider',
          ])
        )
      }, [formik.values])

      const errorKeys = useMemo(() => {
        return JSON.stringify(Object.keys(formik.errors).sort())
      }, [formik.errors])

      useEffect(() => {
        return loadRateRef.current()
      }, [rateRelevantKey, errorKeys, forceRateRefresh])

      const handleEditRate = useCallback(
        async (chooseRate) => {
          setLoading((prev) => ({...prev, updateRate: true}))
          try {
            await OrderService.patch(
              {
                type: 'service',
                order_ids: [id],
                fields: {
                  service: chooseRate?.carrier_service,
                  package_type: chooseRate?.package_type?.value ?? null,
                  estimated_rate: chooseRate?.rate ?? null,
                  is_cheapest_rate: chooseRate?.cheapest ?? false,
                  preset_id:
                    chooseRate?.carrier_service === formik.values.service &&
                    chooseRate?.package_type?.value === formik.values.parcels?.[0]?.package_type
                      ? formik?.values?.preset_id
                      : null,
                },
              },
              {cancelToken: newCancelToken()}
            )
          } catch (error: any) {
            if (isCancel(error)) return
            setValidationErrors(error?.response)
          } finally {
            if (isMounted.current) {
              setLoading((prev) => ({...prev, updateRate: false}))
            }
          }
        },
        [
          formik.values.parcels,
          formik.values?.preset_id,
          formik.values.service,
          id,
          isCancel,
          newCancelToken,
          setValidationErrors,
        ]
      )

      const handleClickRate = (rate) => {
        formik.setValues({
          ...formik.values,
          service: rate?.carrier_service || '',
          estimated_rate: rate?.rate || null,
          is_cheapest_rate: rate?.cheapest || false,
          insurance_option: {
            ...formik.values?.insurance_option,
            insured_fee: rate?.insurance_rate || null,
          },
        })
        setDataRates((prev) => ({
          ...prev,
          chooseRate: rate,
        }))
        if (id && data?.status !== OrderStatus.SHIPPED) {
          handleEditRate(rate)
        }
      }

      const optionsExtraService = useMemo(() => {
        const options =
          addOnServices?.extra_services?.map((item) => ({
            ...item,
            value: item.value?.toString(),
          })) || []
        return [{label: intl.formatMessage({id: 'NONE'}), value: null}, ...options]
      }, [addOnServices?.extra_services, intl])

      const serviceHasPackage = useMemo(() => {
        if (!formik.values.parcels[0]?.package_type || !formik.values?.service) return true
        const {package_types} = carriers?.[formik.values.service]
        if (package_types) {
          return !!find(package_types, {value: formik.values?.parcels?.[0]?.package_type})
        }
        return true
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [carriers, formik.values.parcels?.[0]?.package_type, formik.values.service])

      useImperativeHandle(ref, () => ({
        handleGetRates,
        dataRates: () => dataRates,
        setDataRates,
        loading: () => loading,
        serviceHasPackage: () => serviceHasPackage,
        setPresetService: (service: string | undefined) => setPresetService(service),
      }))

      const hasLoadedRates = !!dataRates?.options?.some((item) => {
        return Number.isFinite(Number(item?.rate))
      })

      return (
        <div
          className={clsx('col-12 col-xxl-5 mt-7 mt-xxl-0 ps-0', {
            'flex-1': width >= LAPTOP_MIN_WIDTH,
          })}
        >
          {statusInsuranceSetting && (
            <div className='bg-warning d-flex justify-content-between rounded-3 px-3 py-5 mb-5 position-relative'>
              <div className='d-flex align-items-start gap-3'>
                <div className='mt-3'>
                  <img
                    src={toAbsoluteUrl('/media/gori/promotions/baeguard/baeguard-shield.svg')}
                    width={42}
                    style={{minWidth: 42, maxWidth: 42}}
                    alt=''
                  />
                </div>

                <div className='fs-6'>
                  <div className={'fw-bolder'}>
                    {intl.formatMessage({id: 'NOTIFICATION_SETTING_BAEGUARD_CONTENT_1'})}
                  </div>
                  <div>{intl.formatMessage({id: 'NOTIFICATION_SETTING_BAEGUARD_CONTENT_2'})}</div>
                  <Link
                    to={ROUTES.HELP_CENTER.BAE_GUARD_AUTOMATED}
                    target='_blank'
                    className='ms-1 text-dark fw-bolder text-decoration-underline'
                  >
                    {intl.formatMessage({id: 'LEARN_MORE'})}
                  </Link>
                </div>
              </div>

              <button
                className='btn-close position-absolute top-0 end-0 m-3'
                onClick={() => setStatusInsuranceSetting(false)}
                type='button'
              />
            </div>
          )}
          <div
            className={clsx('bg-white shadow-sm rounded-2 p-6 mb-24px position-relative', {
              'bg-light': isDisablePage,
            })}
          >
            <div className='d-flex justify-content-between'>
              <div className='d-flex align-items-center'>
                <span className='fs-2 fw-bolder'>
                  {intl.formatMessage({id: 'ADD_ON_SERVICES'})}
                </span>
              </div>
            </div>
            <hr className='bg-gray-600' />
            {(!addOnServices || !isInsuranceSettingLoaded) && (
              <div
                className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded-2 bg-white bg-opacity-75'
                style={{zIndex: 1}}
              >
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
              </div>
            )}
            <div>
              <div className='border-bottom border-gray-300 mb-4'>
                <div className='mb-4 d-flex flex-column gap-4'>
                  <InsuranceSignature
                    formik={formik}
                    isAccept={isAccept}
                    setIsAccept={setIsAccept}
                    disabled={isDisablePage}
                    loadingRate={loading.rate}
                    status={data?.status}
                    isModal={isModal}
                    isErrorUpicAccept={isErrorUpicAccept}
                    setIsErrorUpicAccept={setIsErrorUpicAccept}
                  />
                  <div className='d-flex bg-light p-4 rounded-2'>
                    <SelectFormik
                      label={intl.formatMessage({id: 'SERVICE_OPTION'})}
                      className={clsx('col-6')}
                      labelClassName='col-6 col-form-label fw-bolder'
                      options={optionsExtraService}
                      name='extra_service'
                      emptyDefault={false}
                      formik={formik}
                      hasUseIntl={false}
                      disabled={isDisablePage}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={clsx('bg-white shadow-sm rounded-2 p-6', {
              'bg-light': isDisablePage,
            })}
          >
            <div className='d-flex justify-content-between'>
              <div className='d-flex align-items-center'>
                <span className='fs-2 fw-bolder'>{intl.formatMessage({id: 'RATES'})}</span>
                <OverlayTrigger
                  overlay={
                    <Tooltip id='tooltip-disabled'>
                      {intl.formatMessage({
                        id: 'ESTIMATED_RATE_IS_EXCLUSIVE',
                      })}
                    </Tooltip>
                  }
                >
                  <span>
                    <KTSVG
                      path='/media/gori/orders/exclamation.svg'
                      className='ms-2 mb-1 svg-icon-4 cursor-pointer'
                    />
                  </span>
                </OverlayTrigger>
              </div>
              <Button
                className={clsx(
                  'd-flex gap-1 justify-content-center align-items-center p-1 fs-inherit btn btn-advanced-tertiary fw-bolder cursor-pointer',
                  {
                    'cursor-no-drop': loading.rate || !canGetRates,
                  }
                )}
                event={() => canGetRates && handleGetRates()}
                disabled={loading.rate || !canGetRates}
              >
                <KTSVG
                  path='/media/gori/orders/sync.svg'
                  className='svg-icon-2 svg-icon-teal-clicked'
                  svgClassName={clsx({spin: loading.rate})}
                />
                {intl.formatMessage({id: 'REFRESH'})}
              </Button>
            </div>
            <hr className='bg-gray-600' />
            <div
              className={clsx(
                'd-flex ticket-filter justify-content-between align-items-center p-4 rounded-2 mb-4',
                {
                  'd-none':
                    data?.store_type !== OrderType.SHOPIFY || isEmpty(data?.shipping_method),
                }
              )}
            >
              <span className='fw-bolder'>
                {intl.formatMessage({id: 'CUSTOMER_SHIPPING_SELECTION'})}:
              </span>
              <span className='fw-bold'>{data?.shipping_method}</span>
              <span className='fw-bold'>
                {data?.shipping_rate > 0
                  ? convertCurrency(data?.shipping_rate)
                  : intl.formatMessage({id: 'FREE'})}
              </span>
            </div>
            <div>
              <Rates
                data={dataRates}
                handleClickRate={handleClickRate}
                disabled={isDisablePage}
                dataShipment={name === OrderChildModalName.CREATE_LABEL ? null : data}
                isLoadingRates={loading.rate}
                hideNoRates={carrierRouteSettings?.hide_no_rates ?? true}
                presetService={presetService}
                isInternational={formik.values.international ?? false}
                disabledCarriers={disabledCarrierNames}
                loadingCarriers={loadingCarriers}
                retryingCarriers={retryingCarriers}
                canGetRates={canGetRates}
                onFetchCarrierRates={fetchAdditionalCarrierRates}
                onRetryCarrierRates={retryCarrierRates}
                onExploreAllRates={exploreAllRates}
                onFetchActiveCarriers={fetchActiveCarriers}
              />

              {loading.rate && (
                <div
                  className={clsx('d-flex justify-content-center align-items-center', {
                    'h-400px': !hasLoadedRates,
                    'py-12': hasLoadedRates,
                  })}
                >
                  <img
                    src={toAbsoluteUrl('/media/gori/orders/reload.gif')}
                    className='w-150px'
                    alt='reload-rate'
                  />
                </div>
              )}
              {!dataRates?.options?.length &&
                !loading.rate &&
                data?.status !== OrderStatus.SHIPPED &&
                !Array.isArray(dataRates?.options) && (
                  <EmptyRate
                    {...(!canGetRates && {
                      titleId: 'EMPTY_RATE_VALIDATION',
                      subTextId: 'EMPTY_RATE_VALIDATION_SUB_TEXT',
                    })}
                  />
                )}
            </div>
            {bottomComponent}
          </div>
        </div>
      )
    }
  )
)
